import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { supabase } from '@/lib/supabase';
import { GENERAL_STATUS } from '@/lib/attendanceTypes';
import { formatLocalDateForDisplay } from '@/lib/dateUtils';

// Module-level constants - never recreated
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 8 }, (_, i) => {
  const year = CURRENT_YEAR - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

// UK regions for Gov.uk import
const UK_REGIONS = [
  { value: 'england-and-wales', label: 'England and Wales' },
  { value: 'scotland', label: 'Scotland' },
  { value: 'northern-ireland', label: 'Northern Ireland' }
];

/**
 * Hook to load bank holidays page data with server-side pagination and filtering
 * Replaces 2 sequential queries with 1 optimized RPC call
 *
 * @param {string} companyId - Company ID
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Items per page (default 25)
 * @param {number} options.debounceMs - Search debounce delay (default 300ms)
 * @returns {Object} - { holidays, spaces, stats, loading, error, pagination, filters, refetch }
 */
export function useBankHolidays(companyId, options = {}) {
  const { pageSize: initialPageSize = 25, debounceMs = 300 } = options;

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(() => getStoredPageSize(initialPageSize));
  const setPageSize = useCallback((val) => { _setPageSize(val); setStoredPageSize(val); }, []);

  // Filter state - default to current year
  const [yearFilter, setYearFilter] = useState(CURRENT_YEAR.toString());
  const [spaceFilter, setSpaceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs for request management
  const loadingRef = useRef(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, debounceMs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [yearFilter, spaceFilter]);

  /**
   * Load bank holidays data from server
   */
  const loadData = useCallback(async () => {
    if (!companyId) {
      console.log('[useBankHolidays] Missing companyId');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useBankHolidays] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useBankHolidays] Loading data with single RPC call...');
      console.time('[useBankHolidays] RPC call duration');

      const offset = (page - 1) * pageSize;

      // Build filter params - 'all' means no filter
      const yearParam = yearFilter && yearFilter !== 'all' ? parseInt(yearFilter, 10) : null;
      const spaceParam = spaceFilter && spaceFilter !== 'all' ? spaceFilter : null;
      const searchParam = debouncedSearch || null;

      // Single RPC call replaces 2 sequential queries!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_bank_holidays_page',
        {
          p_company_id: companyId,
          p_year: yearParam,
          p_project_id: spaceParam,
          p_search: searchParam,
          p_limit: pageSize,
          p_offset: offset
        }
      );

      console.timeEnd('[useBankHolidays] RPC call duration');

      if (rpcError) {
        console.error('[useBankHolidays] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useBankHolidays] RPC response:', {
        holidaysCount: rpcData?.holidays?.length || 0,
        spacesCount: rpcData?.spaces?.length || 0,
        totalCount: rpcData?.total_count || 0,
        filteredCount: rpcData?.filtered_count || 0,
        stats: rpcData?.stats
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useBankHolidays] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId, page, pageSize, yearFilter, spaceFilter, debouncedSearch]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  /**
   * Change page size and reset to first page
   */
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  /**
   * Set year filter
   */
  const handleYearChange = useCallback((year) => {
    setYearFilter(year);
  }, []);

  /**
   * Set space filter
   */
  const handleSpaceChange = useCallback((space) => {
    setSpaceFilter(space);
  }, []);

  /**
   * Format date for display
   */
  const formatDate = useCallback((dateStr) => {
    return formatLocalDateForDisplay(dateStr);
  }, []);

  // Computed pagination values
  const totalPages = useMemo(() => {
    const filteredCount = data?.filtered_count || 0;
    return Math.ceil(filteredCount / pageSize);
  }, [data?.filtered_count, pageSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    holidays: data?.holidays || [],
    spaces: data?.spaces || [],
    stats: data?.stats || {
      total: 0,
      this_year: 0,
      upcoming: 0,
      company_wide: 0
    },

    // Subscribed regions from company settings
    subscribedRegions: data?.subscribed_regions || ['england-and-wales'],

    // Loading state
    loading,
    error,

    // Pagination
    pagination: {
      page,
      pageSize,
      totalPages,
      totalItems: data?.total_count || 0,
      filteredItems: data?.filtered_count || 0,
      setPage,
      setPageSize: handlePageSizeChange
    },

    // Filters
    filters: {
      year: yearFilter,
      space: spaceFilter,
      search: searchTerm,
      setYear: handleYearChange,
      setSpace: handleSpaceChange,
      setSearch: setSearchTerm
    },

    // Helpers
    formatDate,

    // Actions
    refetch: loadData,

    // Static data (module-level constants)
    years: YEARS,
    ukRegions: UK_REGIONS
  };
}

/**
 * Hook for bank holiday actions (create, update, delete, region subscriptions)
 * Separated to keep concerns clean
 *
 * @param {string} companyId - Company ID
 * @param {Function} onSuccess - Callback after successful action
 * @returns {Object} - { createHoliday, updateHoliday, deleteHoliday, updateRegionSubscriptions, processing }
 */
export function useBankHolidayActions(companyId, onSuccess) {
  const [processing, setProcessing] = useState(false);

  /**
   * Create a new bank holiday
   */
  const createHoliday = useCallback(async (holidayData) => {
    if (!holidayData?.name || !holidayData?.date) {
      return { success: false, error: 'Name and date are required' };
    }

    setProcessing(true);

    try {
      if (holidayData.applyTo === 'company' || !holidayData.selectedSpaces?.length) {
        // Single company-wide holiday
        const { error } = await supabase
          .from('bank_holidays')
          .insert({
            name: holidayData.name.trim(),
            date: holidayData.date,
            recurring: holidayData.recurring || false,
            project_id: null,
            company_id: companyId,
            status: GENERAL_STATUS.ACTIVE,
            created_date: new Date().toISOString(),
            updated_date: new Date().toISOString()
          });

        if (error) throw error;
      } else {
        // Multiple space-specific holidays
        const holidays = holidayData.selectedSpaces.map(spaceId => ({
          name: holidayData.name.trim(),
          date: holidayData.date,
          recurring: holidayData.recurring || false,
          project_id: spaceId,
          company_id: companyId,
          status: GENERAL_STATUS.ACTIVE,
          created_date: new Date().toISOString(),
          updated_date: new Date().toISOString()
        }));

        const { error } = await supabase
          .from('bank_holidays')
          .insert(holidays);

        if (error) throw error;
      }

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useBankHolidayActions] Create error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [companyId, onSuccess]);

  /**
   * Update an existing bank holiday
   */
  const updateHoliday = useCallback(async (holidayId, updates) => {
    if (!holidayId) return { success: false, error: 'Missing holiday ID' };

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('bank_holidays')
        .update({
          name: updates.name?.trim(),
          date: updates.date,
          recurring: updates.recurring || false,
          project_id: updates.applyTo === 'company' ? null : updates.selectedSpaces?.[0] || null,
          updated_date: new Date().toISOString()
        })
        .eq('id', holidayId);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useBankHolidayActions] Update error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  /**
   * Delete a bank holiday
   */
  const deleteHoliday = useCallback(async (holidayId) => {
    if (!holidayId) return { success: false, error: 'Missing holiday ID' };

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('bank_holidays')
        .delete()
        .eq('id', holidayId);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useBankHolidayActions] Delete error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  /**
   * Update company's subscribed UK regions for bank holidays
   * This controls which Gov.uk holidays are shown to the company
   */
  const updateRegionSubscriptions = useCallback(async (regions) => {
    if (!regions || !Array.isArray(regions)) {
      return { success: false, error: 'Invalid regions array' };
    }

    if (regions.length === 0) {
      return { success: false, error: 'Please select at least one region' };
    }

    setProcessing(true);

    try {
      // Get current company settings
      const { data: company, error: fetchError } = await supabase
        .from('companies')
        .select('settings')
        .eq('id', companyId)
        .single();

      if (fetchError) throw fetchError;

      // Update settings with new regions
      const newSettings = {
        ...(company?.settings || {}),
        bank_holiday_regions: regions
      };

      const { error: updateError } = await supabase
        .from('companies')
        .update({
          settings: newSettings,
          updated_date: new Date().toISOString()
        })
        .eq('id', companyId);

      if (updateError) throw updateError;

      if (onSuccess) onSuccess();
      return { success: true, regions };
    } catch (err) {
      console.error('[useBankHolidayActions] Update regions error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [companyId, onSuccess]);

  return {
    createHoliday,
    updateHoliday,
    deleteHoliday,
    updateRegionSubscriptions,
    processing
  };
}
